/**
 * GlobalView — Camera Service (§11.4)
 *
 * Utility functions for camera navigation.
 * Uses globeStore for viewer access.
 */

import * as Cesium from 'cesium';

/**
 * Subscribe to camera changes and report position updates.
 *
 * @param viewer CesiumJS Viewer instance
 * @param onUpdate Callback with (lat, lon, alt, heading, pitch)
 * @returns Remove listener function
 */
export function watchCameraPosition(
  viewer: Cesium.Viewer,
  onUpdate: (pos: {
    lat: number;
    lon: number;
    alt: number;
    heading: number;
    pitch: number;
  }) => void,
): () => void {
  const handler = () => {
    const camera = viewer.camera;
    const carto = Cesium.Cartographic.fromCartesian(camera.positionWC);

    onUpdate({
      lat: Cesium.Math.toDegrees(carto.latitude),
      lon: Cesium.Math.toDegrees(carto.longitude),
      alt: carto.height,
      heading: Cesium.Math.toDegrees(camera.heading),
      pitch: Cesium.Math.toDegrees(camera.pitch),
    });
  };

  // Throttle updates to ~10 fps max
  let frameId: number | null = null;
  const throttledHandler = () => {
    if (frameId !== null) return;
    frameId = requestAnimationFrame(() => {
      handler();
      frameId = null;
    });
  };

  viewer.camera.changed.addEventListener(throttledHandler);
  viewer.camera.moveEnd.addEventListener(handler);

  // Initial position
  handler();

  return () => {
    viewer.camera.changed.removeEventListener(throttledHandler);
    viewer.camera.moveEnd.removeEventListener(handler);
    if (frameId !== null) cancelAnimationFrame(frameId);
  };
}

/**
 * Track mouse cursor geo-position on the globe.
 *
 * @param viewer CesiumJS Viewer instance
 * @param onUpdate Callback with cursor position or null
 * @returns Destroy function
 */
export function watchCursorPosition(
  viewer: Cesium.Viewer,
  onUpdate: (pos: { lat: number; lon: number } | null) => void,
): () => void {
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  handler.setInputAction((movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
    const cartesian = viewer.scene.pickPosition(movement.endPosition);
    if (!cartesian) {
      // Try ray-pick on ellipsoid
      const ray = viewer.camera.getPickRay(movement.endPosition);
      if (ray) {
        const ellipsoidPos = viewer.scene.globe.pick(ray, viewer.scene);
        if (ellipsoidPos) {
          const carto = Cesium.Cartographic.fromCartesian(ellipsoidPos);
          onUpdate({
            lat: Cesium.Math.toDegrees(carto.latitude),
            lon: Cesium.Math.toDegrees(carto.longitude),
          });
          return;
        }
      }
      onUpdate(null);
      return;
    }

    const carto = Cesium.Cartographic.fromCartesian(cartesian);
    onUpdate({
      lat: Cesium.Math.toDegrees(carto.latitude),
      lon: Cesium.Math.toDegrees(carto.longitude),
    });
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  return () => handler.destroy();
}

/**
 * Watch camera viewport and send bounding box updates to the server.
 * Debounced to avoid flooding the WS with updates during camera movement.
 *
 * @param viewer CesiumJS Viewer instance
 * @param onViewport Callback with viewport bounding box
 * @returns Remove listener function
 */
export function watchViewport(
  viewer: Cesium.Viewer,
  onViewport: (vp: { lamin: number; lomin: number; lamax: number; lomax: number } | null) => void,
): () => void {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const computeViewport = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      try {
        const camera = viewer.camera;
        const rect = camera.computeViewRectangle(viewer.scene.globe.ellipsoid);

        if (!rect) {
          // Camera is too high or looking at space — use global
          onViewport(null);
          return;
        }

        const lamin = Cesium.Math.toDegrees(rect.south);
        const lamax = Cesium.Math.toDegrees(rect.north);
        const lomin = Cesium.Math.toDegrees(rect.west);
        const lomax = Cesium.Math.toDegrees(rect.east);

        // If the viewport is nearly full globe, send null (global)
        const latSpan = lamax - lamin;
        const lonSpan = lomax - lomin;
        if (latSpan > 150 || lonSpan > 300) {
          onViewport(null);
        } else {
          onViewport({ lamin, lomin, lamax, lomax });
        }
      } catch {
        onViewport(null);
      }
    }, 2000); // 2s debounce — only send after camera stops moving
  };

  viewer.camera.moveEnd.addEventListener(computeViewport);

  // Send initial viewport
  computeViewport();

  return () => {
    viewer.camera.moveEnd.removeEventListener(computeViewport);
    if (debounceTimer) clearTimeout(debounceTimer);
  };
}
